import { createApp, h, resolveDynamicComponent } from 'vue'
import { render } from '@/common/util/templateCompiler'
import { isString } from 'underscore'

function resolveEl(el) {
  return typeof el === 'string' ? document.querySelector(el) : el
}

function removeComments(node) {
  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType === Node.COMMENT_NODE) {
      node.removeChild(child)
    } else {
      removeComments(child)
    }
  })
}

export default (locator, element, options) => {
  const { el, ...componentOptions } = options

  const app = createApp({
    ...componentOptions,
    render() {
      if (isString(element)) return h(resolveDynamicComponent(element))
      else return render(this, element)
    }
  })

  locator.$configureApp(app)

  if (el) {
    // vue3 does not replace the element "in place"
    // the root component element goes
    // inside the passed el instead of replacing it
    // using a temporary element and replaceWith to get old vue2 behavior
    const target = resolveEl(el)

    const temp = document.createElement('div')
    target.parentNode.insertBefore(temp, target)
    const rootInstance = app.mount(temp)
    const nodes = Array.from(temp.childNodes)
    target.replaceWith(...nodes)
    temp.remove()

    setLegacyUnmount({ app, rootInstance })
  }

  return app
}

/**
 * Vue3 unmount does remove the root component element
 * This function is a wrapper to restore the root element
 * after the unmount see KJS-6343
 */
function setLegacyUnmount({ app, rootInstance }) {
  const nativeUnmount = app.unmount.bind(app)
  let preservedRoot = null
  let unmounted = false
  app.legacyUnmount = () => {
    if (unmounted) {
      return preservedRoot
    }

    // Resolve it now, not at unmount time.
    const currentRoot = rootInstance.$el

    if (!(currentRoot instanceof Element)) {
      console.warn(
        'Cannot perform legacy unmount: the current root is not an element'
      )
      return
    }

    if (!currentRoot.parentNode) {
      nativeUnmount()
      unmounted = true
      return
    }

    // Snapshot the complete current Vue-generated HTML.
    preservedRoot = currentRoot.cloneNode(true)
    // remove generated vue3 anchor comment just in case
    removeComments(preservedRoot)

    // External temporary marker for the exact DOM position.
    const anchor = document.createComment('legacy-unmount')
    currentRoot.before(anchor)

    nativeUnmount()
    unmounted = true

    // Restore the inert Vue-generated HTML.
    anchor.replaceWith(preservedRoot)

    return
  }
}
